import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../store/store'

export interface UseRoleGuardResult {
  hasAccess: boolean
  loading: boolean
  initialized: boolean
  error: string | null
  isAdmin: boolean
  isApprover: boolean
  isApproverProxy: boolean
  isRequestor: boolean
  isRequestorProxy: boolean
  // Scoped admin flags (grant access to one Assign-Proxy feature only)
  isAPAdmin: boolean
  isFEAdmin: boolean
  isRPAdmin: boolean
  // Convenience access flags (derived from roles)
  canAccessAdminPage: boolean
  canAccessApproverPage: boolean
  canAccessRequestorPage: boolean
  canAccessRequestorProxyFeature: boolean
}

export const useRoleGuard = (allowedRoles: string[]): UseRoleGuardResult => {
  const profileState = useSelector((state: RootState) => state.profile)
  const profile = profileState?.data
  const roles = profile?.roles

  return useMemo(() => {
    const loading = profileState.loading
    const initialized = profileState.initialized
    const error = profileState.error ?? null

    // Extract individual role flags from API response
    const isAdmin = Boolean(roles?.isAdmin)
    const isApprover = Boolean(roles?.isApprover)
    const isApproverProxy = Boolean(roles?.isApproverProxy)
    // Backend does not send `isRequestor`; everyone is requestor by default
    const isRequestor = true
    const isRequestorProxy = Boolean(roles?.isRequestorProxy)
    // Scoped admin flags: each grants access to one Assign-Proxy feature only
    const isAPAdmin = Boolean(roles?.isAPAdmin)
    const isFEAdmin = Boolean(roles?.isFEAdmin)
    const isRPAdmin = Boolean(roles?.isRPAdmin)

    // Convenience: compute explicit access flags with clear names
    const canAccessAdminPage = isAdmin
    const canAccessApproverPage = isApprover || isApproverProxy
    const canAccessApproverProxyFeature = isApproverProxy
    const canAccessRequestorPage = true // everyone can access requestor pages
    const canAccessRequestorProxyFeature = isRequestorProxy

    // Map role keys (used by callers) to the derived access flags
    const roleAccessMap: Record<string, boolean> = {
      admin: canAccessAdminPage,
      approver: canAccessApproverPage,
      approverProxy: canAccessApproverProxyFeature,
      requestor: canAccessRequestorPage,
      requestorProxy: canAccessRequestorProxyFeature,
      apAdmin: isAPAdmin,
      feAdmin: isFEAdmin,
      rpAdmin: isRPAdmin,
    }

    // Admin override: admins can access everything
    const hasAdminOverride = isAdmin

    // Security: If no roles specified, deny access (prevent accidental bypass)
    if (allowedRoles.length === 0) {
      console.warn('[Security] Access attempted with empty allowedRoles')
      return {
        hasAccess: false,
        loading,
        initialized,
        error,
        isAdmin,
        isApprover,
        isApproverProxy,
        isRequestor,
        isRequestorProxy,
        isAPAdmin,
        isFEAdmin,
        isRPAdmin,
        canAccessAdminPage,
        canAccessApproverPage,
        canAccessRequestorPage,
        canAccessRequestorProxyFeature,
      }
    }

    // Check if user has any allowed role using Set for O(1) lookup
    const allowedRolesSet = new Set(allowedRoles)
    // Debug: show array snapshot instead of raw Set to avoid ambiguous logs
    // console.debug('useRoleGuard allowedRoles:', Array.from(allowedRolesSet))
    // Grant access if: user is admin (superuser) OR user has one of the allowed roles
    const hasAccess =
      hasAdminOverride ||
      Array.from(allowedRolesSet).some((role) => !!roleAccessMap[role])

    if (!hasAccess && initialized) {
      console.warn('[Security] Unauthorized access attempt', {
        allowedRoles: Array.from(allowedRolesSet),
        userRoles: {
          isAdmin,
          isApprover,
          isApproverProxy,
          isRequestor,
          isRequestorProxy,
        },
      })
    }

    return {
      hasAccess,
      loading,
      initialized,
      error,
      isAdmin,
      isApprover,
      isApproverProxy,
      isRequestor,
      isRequestorProxy,
      isAPAdmin,
      isFEAdmin,
      isRPAdmin,
      canAccessAdminPage,
      canAccessApproverPage,
      canAccessRequestorPage,
      canAccessRequestorProxyFeature,
    }
  }, [profileState.loading, profileState.initialized, profileState.error, roles, allowedRoles])
}
